import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Splitting from "splitting";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";

import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import firmengebaeudeImg from "@/assets/firmengebaeude-zweering-emmerich.webp";
import wofuerBgAsset from "@/assets/wofuer-wir-stehen-wallbox.png";
import ctaLernenBgAsset from "@/assets/cta-lernen-sie-uns-kennen.png";
import golfclubMoylandLogoAsset from "@/assets/golfclub-moyland-logo.png";
import fortunaEltenLogoAsset from "@/assets/fortuna-elten-logo.png";
import jugendMillingenLogoAsset from "@/assets/jugend-millingen-logo.png";
import jsgEmmerichLogoAsset from "@/assets/jsg-emmerich-logo.png";
import vfbRheingoldLogoAsset from "@/assets/vfb-rheingold-logo.png";
gsap.registerPlugin(ScrollTrigger);

/* ────────────────── Reveal hooks (same logic as LeistungsseiteTemplate) ────────────────── */

function useSplitReveal(selector: string) {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targets = Array.from(document.querySelectorAll<HTMLElement>(selector));
    if (!targets.length) return;
    const triggers: ScrollTrigger[] = [];

    targets.forEach((el) => {
      if (prefersReduced) {
        gsap.fromTo(
          el,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 0.6,
            scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
          }
        );
        return;
      }
      const result = Splitting({ target: el, by: "words" });
      const words = result[0]?.words ?? [];
      if (!words.length) return;
      gsap.set(words, { opacity: 0, y: 20, display: "inline-block" });
      const tween = gsap.to(words, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out",
        stagger: 0.04,
        scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
      });
      if (tween.scrollTrigger) triggers.push(tween.scrollTrigger);
    });

    return () => triggers.forEach((t) => t.kill());
  }, [selector]);
}

function useImageReveal(selector: string) {
  useEffect(() => {
    const imgs = Array.from(document.querySelectorAll<HTMLElement>(selector));
    imgs.forEach((img) => {
      gsap.fromTo(
        img,
        { opacity: 0, scale: 1.05 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: img,
            start: "top 90%",
            toggleActions: "play none none none",
            onEnter: () => img.classList.add("is-inview"),
          },
        }
      );
    });
  }, [selector]);
}

function useTimelineReveal(olRef: React.RefObject<HTMLOListElement | null>) {
  useEffect(() => {
    if (!olRef.current) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ol = olRef.current;
    const lines = Array.from(ol.querySelectorAll<HTMLElement>("[data-timeline-line]"));
    const dots = Array.from(ol.querySelectorAll<HTMLElement>("[data-timeline-dot]"));
    const texts = Array.from(ol.querySelectorAll<HTMLElement>("[data-timeline-text]"));

    if (prefersReduced) {
      gsap.set(lines, { scaleY: 1 });
      gsap.set([...dots, ...texts], { opacity: 1, y: 0, scale: 1 });
      return;
    }

    gsap.set(lines, { transformOrigin: "top center", scaleY: 0 });
    gsap.set(dots, { opacity: 0, scale: 0.8 });
    gsap.set(texts, { opacity: 0, y: 24 });

    const triggers: ScrollTrigger[] = [];

    // Scroll-driven line draw (progressive, top-to-bottom, once)
    const lineTween = gsap.fromTo(
      lines,
      { scaleY: 0 },
      {
        scaleY: 1,
        ease: "none",
        stagger: 0.05,
        scrollTrigger: {
          trigger: ol,
          start: "top 75%",
          end: "bottom 60%",
          scrub: true,
          onLeave: () => {
            lineTween.kill();
            if (lineTween.scrollTrigger) lineTween.scrollTrigger.kill();
            gsap.set(lines, { scaleY: 1 });
          },
        },
      }
    );
    if (lineTween.scrollTrigger) triggers.push(lineTween.scrollTrigger);

    // Each milestone dot + text fades in once it enters the viewport
    [...dots, ...texts].forEach((el) => {
      const tween = gsap.to(el, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 82%",
          toggleActions: "play none none none",
        },
      });
      if (tween.scrollTrigger) triggers.push(tween.scrollTrigger);
    });

    return () => triggers.forEach((t) => t.kill());
  }, [olRef]);
}

/* ────────────────── Werte-Cards Data ────────────────── */

const werte = [
  {
    icon: "ri-links-line",
    title: "AUS EINER HAND",
    text: "Planung, Installation und Betreuung — alles aus einem Haus, ein Ansprechpartner.",
  },
  {
    icon: "ri-shield-check-line",
    title: "MEISTERQUALITÄT",
    text: "Elektro-Meisterbetrieb mit dem Anspruch, jede Anlage sauber und normgerecht auszuführen.",
  },
  {
    icon: "ri-map-pin-line",
    title: "REGIONAL VERWURZELT",
    text: "Zuhause in Emmerich am Rhein und Umgebung — für die Menschen und Betriebe der Region.",
  },
];

export default function UeberUns() {
  useSmoothScroll();
  useSplitReveal("[data-split-reveal]");
  useImageReveal("[data-image-reveal]");
  const timelineRef = useRef<HTMLOListElement>(null);
  useTimelineReveal(timelineRef);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Archivo, sans-serif" }}>
      <Seo
        title="Über uns | Zweering Elektrotechnik Emmerich am Rhein"
        description="Der Meisterbetrieb hinter Ihrer Energie — Zweering Elektrotechnik aus Emmerich am Rhein: Elektrotechnik, Photovoltaik und Speicher aus einer Hand."
        canonical="https://zweering.de/ueber-uns"
        breadcrumbs={[
          { name: "Startseite", url: "https://zweering.de/" },
          { name: "Über uns", url: "https://zweering.de/ueber-uns" },
        ]}
      />
      <Header />



      <main id="main-content">
        {/* 1. HERO (~70vh) */}
        <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden">
          <img
            src={firmengebaeudeImg}
            alt="Firmengebäude von Zweering Elektrotechnik in Emmerich am Rhein – beleuchtete Backsteinfassade in der blauen Stunde mit Smart-Firmenfahrzeug auf dem Parkplatz"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
          <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-10 lg:px-14 pb-16 md:pb-20">
            <div className="max-w-7xl mx-auto w-full">
              <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                UNTERNEHMEN
              </p>
              <h1
                data-split-reveal
                className="font-bold uppercase text-white leading-[0.95] max-w-5xl break-words"
                style={{
                  fontSize: "clamp(1.875rem, 8vw, 5.25rem)",
                  letterSpacing: "clamp(-0.6px, -0.18vw, -2.4px)",
                }}
              >
                Der Meisterbetrieb hinter Ihrer Energie
              </h1>
              <p className="text-white/85 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
                Elektrotechnik aus Emmerich am Rhein — bodenständig, modern und aus einer
                Hand.
              </p>
            </div>
          </div>
        </section>

        {/* 2. GESCHICHTE / ZWEI GENERATIONEN */}
        {/* {{GRÜNDUNGSJAHR_KLÄREN}} — offen: "Meisterbetrieb seit 1991" vs. "Gründung 1999".
            Bis zur Klärung wird im sichtbaren Text bewusst KEINE Jahreszahl genannt. */}
        <section className="bg-white py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                Geschichte
              </p>
              <h2
                data-split-reveal
                className="font-bold uppercase text-[#0A0A0A] leading-[0.95] break-words"
                style={{
                  fontSize: "clamp(1.5rem, 6vw, 3.5rem)",
                  letterSpacing: "clamp(-0.5px, -0.15vw, -2px)",
                }}
              >
                ZWEI GENERATIONEN. EIN ANSPRUCH.
              </h2>
              <p className="mt-6 text-foreground-600 text-base md:text-lg leading-relaxed">
                Gegründet von Lutz Zweering, seit 2020 geführt von Bastian Zweering — der
                Betrieb verbindet handwerkliche Erfahrung mit modernem Energie-Knowhow.
              </p>
              <p className="mt-4 text-foreground-600 text-base md:text-lg leading-relaxed">
                Aus dem klassischen Elektro-Handwerk ist über die Jahre ein Meisterbetrieb
                geworden, der heute komplette Energiesysteme plant, baut und betreut — von
                der ersten Beratung bis zur Wartung Jahre später.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden bg-[#F4F4F4] aspect-[4/3]">
              <img
                src="https://www.zweering-elektrotechnik.de/wp-content/uploads/2025/11/IMG_0093.webp"
                alt="Bastian Zweering im Büro von Zweering Elektrotechnik in Emmerich am Rhein"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* 2b. TIMELINE — VIER MEILENSTEINE */}
        <section className="bg-white py-36 md:py-40 px-6 md:px-10 lg:px-14 border-t border-black/5">
          <div className="max-w-7xl mx-auto">
            <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
              Geschichte
            </p>
            <h2
              data-split-reveal
              className="font-bold uppercase text-[#0A0A0A] leading-[0.95] max-w-4xl break-words"
              style={{ fontSize: "clamp(1.5rem, 6vw, 3.5rem)", letterSpacing: "clamp(-0.5px, -0.15vw, -2px)" }}
            >
              <span className="block">FÜNF MEILENSTEINE.</span>
              <span className="block">EINE FAMILIE.</span>
            </h2>
            <p className="mt-6 text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl">
              Vom Familienbetrieb zum Energiesystem-Partner am Niederrhein.
            </p>

            <ol ref={timelineRef} className="mt-16 md:mt-20">
              {[
                {
                  year: "1999",
                  title: "FIRMENGRÜNDUNG",
                  text: "Lutz Zweering gründet das Unternehmen in Emmerich am Rhein und legt den Grundstein für die Familientradition im Elektrohandwerk.",
                },
                {
                  year: "2020",
                  title: "ÜBERNAHME DURCH BASTIAN ZWEERING",
                  text: "Bastian Zweering übernimmt die Leitung in zweiter Generation und führt den Betrieb mit frischen Ideen und Innovationen in die Zukunft.",
                },
                {
                  year: "2021",
                  title: "SPEZIALISIERUNG AUF PHOTOVOLTAIK",
                  text: "Das Unternehmen erweitert sein Spektrum und spezialisiert sich auf PV, Speicher und nachhaltige Energielösungen — der Grundstein für den heutigen Energiesystem-Partner.",
                },
                {
                  year: "2024",
                  title: "NEUES FIRMENGEBÄUDE",
                  text: "Umzug in das neue Firmengebäude in Emmerich — Platz für Wachstum, moderne Werkstatt und fortschrittliche Arbeitsbedingungen.",
                },
                {
                  year: "2026",
                  title: "NEUE GESCHÄFTSFELDER",
                  text: "Mit der neuen Website starten wir weitere Leistungen: Speicher, G2 Contracting und moderne Energiesysteme für Gewerbe und Privat.",
                },
              ].map((item, idx, arr) => (
                <li
                  key={item.year}
                  className="relative grid grid-cols-[70px_1fr] md:grid-cols-[200px_1fr] gap-4 md:gap-10 pb-14 md:pb-20 last:pb-0"
                >
                  <div>
                    <span
                      className="block font-bold text-[#0A0A0A] leading-none"
                      style={{ fontSize: "clamp(1.5rem, 4.5vw, 3.5rem)", letterSpacing: "-1.5px" }}
                    >
                      {item.year}
                    </span>
                  </div>

                  <div className="relative pl-8 md:pl-10">
                    <span
                      data-timeline-dot
                      className="absolute left-0 top-2 w-3 h-3 rounded-full bg-[#4CAF50] ring-4 ring-[#4CAF50]/15 -translate-x-1/2"
                    />
                    {idx < arr.length - 1 && (
                      <span
                        data-timeline-line
                        className="absolute left-0 top-6 bottom-0 w-px bg-[#4CAF50]/30 -translate-x-1/2 origin-top"
                      />
                    )}
                    <div data-timeline-text>
                      <h3
                        className="font-bold uppercase text-[#0A0A0A] text-[20px] md:text-[26px] leading-[1.15]"
                        style={{ letterSpacing: "-0.8px" }}
                      >
                        {item.title}
                      </h3>
                      <p className="mt-3 text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 3. WOFÜR WIR STEHEN — 3 Werte-Cards */}
        <section className="relative py-36 md:py-40 px-6 md:px-10 lg:px-14 overflow-hidden">
          <img
            src={wofuerBgAsset}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#F4F4F4]/80 backdrop-blur-[2px]" />
          <div className="relative max-w-7xl mx-auto">
            <h2
              data-split-reveal
              className="font-bold uppercase text-[#0A0A0A] leading-[0.95] max-w-3xl break-words"
              style={{ fontSize: "clamp(1.5rem, 6vw, 3.5rem)", letterSpacing: "clamp(-0.5px, -0.15vw, -2px)" }}
              data-applied

            >
              Wofür wir stehen
            </h2>
            <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
              {werte.map((w) => (
                <div
                  key={w.title}
                  className="rounded-2xl bg-white p-8 md:p-10 flex flex-col h-full"
                >
                  <span className="w-12 h-12 rounded-full bg-[#4CAF50]/10 flex items-center justify-center mb-6">
                    <i className={`${w.icon} text-[#4CAF50] text-xl w-6 h-6 flex items-center justify-center`} />
                  </span>
                  <h3
                    className="font-bold uppercase text-[#0A0A0A] text-[22px] md:text-[26px] leading-[1.1]"
                    style={{ letterSpacing: "-1px" }}
                  >
                    {w.title}
                  </h3>
                  <p className="mt-4 text-foreground-600 text-base leading-relaxed">{w.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. ZAHLEN / TRUST */}
        <section className="bg-white py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6 text-center md:text-left">
              <div>
                <p className="text-[#4CAF50] text-xs uppercase tracking-widest font-medium mb-3">
                  Vertrauen
                </p>
                <p
                  className="font-bold uppercase text-[#0A0A0A] text-[28px] sm:text-[40px] md:text-[56px] leading-[0.95]"
                  style={{ letterSpacing: "-1.8px" }}
                >
                  ★ 5,0
                </p>
                <p className="mt-2 text-foreground-600 text-sm uppercase tracking-wider">
                  Google-Bewertung
                </p>
              </div>
              <div>
                <p className="text-[#4CAF50] text-xs uppercase tracking-widest font-medium mb-3">
                  Erfahrung
                </p>
                <p
                  className="font-bold uppercase text-[#0A0A0A] text-[28px] sm:text-[40px] md:text-[56px] leading-[0.95]"
                  style={{ letterSpacing: "-1.8px" }}
                >
                  ÜBER 400
                </p>
                <p className="mt-2 text-foreground-600 text-sm uppercase tracking-wider">
                  PV-Anlagen
                </p>
              </div>
              <div>
                <p className="text-[#4CAF50] text-xs uppercase tracking-widest font-medium mb-3">
                  Region
                </p>
                <p
                  className="font-bold uppercase text-[#0A0A0A] text-[28px] md:text-[36px] leading-[1.05]"
                  style={{ letterSpacing: "-1.4px" }}
                >
                  EMMERICH AM RHEIN & UMGEBUNG
                </p>
                <p className="mt-2 text-foreground-600 text-sm uppercase tracking-wider">
                  Niederrhein
                </p>
              </div>
            </div>
            {/* {{ZAHL_KLÄREN}} — weitere Kennzahlen (Mitarbeiteranzahl, Gründungsjahr,
                installierte kWp etc.) sind noch zu bestätigen und bewusst nicht im
                sichtbaren Text genannt. */}
          </div>
        </section>

        {/* 5. ENERGIESYSTEM-VERSPRECHEN (dunkel) */}
        <section className="bg-[#0A0A0A] py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <h2
              data-split-reveal
              className="font-bold uppercase text-white leading-[0.95] max-w-4xl break-words"
              style={{ fontSize: "clamp(1.75rem, 7vw, 4.5rem)", letterSpacing: "clamp(-0.6px, -0.18vw, -2.4px)" }}
              data-applied

            >
              Vom Elektriker zum Energiesystem-Partner
            </h2>
            <p className="mt-8 text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">
              Zweering denkt Energie als System. Photovoltaik, Speicher, Wallbox und Smart
              Home greifen ineinander — geplant, installiert und betreut aus einer Hand.
              Das ist der Anspruch, mit dem wir Ihr Zuhause für die kommenden Jahre
              ausstatten.
            </p>
          </div>
        </section>

        {/* 5b. ENGAGEMENT — Verwurzelt im Niederrhein */}
        <section className="bg-white py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
              Engagement
            </p>
            <h2
              data-split-reveal
              className="font-bold uppercase text-[#0A0A0A] leading-[0.95] max-w-4xl break-words"
              style={{ fontSize: "clamp(1.5rem, 6vw, 3.5rem)", letterSpacing: "clamp(-0.5px, -0.15vw, -2px)" }}
            >
              VERWURZELT IM NIEDERRHEIN.
            </h2>
            <p className="mt-6 text-foreground-600 text-base md:text-lg leading-relaxed max-w-3xl">
              Seit über 20 Jahren unterstützen wir Vereine, Jugendarbeit und Initiativen in
              Emmerich und Umgebung. Über 50.000 € sind so in die Region geflossen — weil wir
              wissen, dass eine starke Gemeinschaft die beste Grundlage für unser Handwerk
              ist.
            </p>

            {/* Vier Schwerpunkte — Säulen */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 border-t border-black/10 pt-12">
              {[
                {
                  title: "SPORTFÖRDERUNG",
                  text: "Wir unterstützen lokale Sportvereine, um jungen Talenten eine Chance zu geben und den Gemeinschaftssinn zu stärken.",
                },
                {
                  title: "KULTURELLE PROJEKTE",
                  text: "Neben dem Sport fördern wir kulturelle Initiativen, die das Leben in unserer Region bereichern.",
                },
                {
                  title: "BILDUNGS\u00ADUNTERSTÜTZUNG",
                  text: "Bildung ist uns wichtig. Wir engagieren uns in Projekten, die Jugendlichen bessere Zukunftschancen ermöglichen.",
                },
                {
                  title: "SOZIALE VERANTWORTUNG",
                  text: "Unsere Unterstützung geht über finanzielle Hilfen hinaus — wir engagieren uns aktiv in sozialen Projekten der Region.",
                },
              ].map((p) => (
                <div key={p.title} className="border-t-2 border-[#4CAF50] pt-5">
                  <h3
                    className="font-bold uppercase text-[#0A0A0A] text-[15px] md:text-[16px] leading-[1.2]"
                    style={{ letterSpacing: "-0.3px" }}
                  >
                    {p.title}
                  </h3>
                  <p className="mt-3 text-foreground-600 text-sm md:text-[15px] leading-relaxed">
                    {p.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Mini-Stats */}
            <div className="mt-16 flex flex-col sm:flex-row sm:justify-end gap-8 border-t border-black/10 pt-10">
              {[
                { value: "6", label: "Jugendmannschaften unterstützt" },
                { value: "5", label: "Sponsoring-Partner" },
              ].map((s) => (
                <div key={s.label} className="text-right">
                  <p
                    className="font-bold text-[#0A0A0A] leading-none"
                    style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", letterSpacing: "-1px" }}
                  >
                    {s.value}
                  </p>
                  <p className="mt-3 text-foreground-600 text-xs uppercase tracking-widest">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Vereins-Cards mit Vereinsfarben */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                {
                  name: "Golfclub Moyland",
                  short: "Golfclub",
                  url: "https://www.landgolfclub.de/",
                  color: "#FFFFFF",
                  textOnColor: "#0A0A0A",
                  image: golfclubMoylandLogoAsset,
                },
                {
                  name: "Jugend Fortuna Elten",
                  short: "Fortuna Elten",
                  url: "https://fortuna-elten.de/",
                  color: "#FFFFFF",
                  textOnColor: "#0A0A0A",
                  image: fortunaEltenLogoAsset,
                },
                {
                  name: "Jugend Millingen",
                  short: "Jugend Millingen",
                  url: "#",
                  color: "#FFFFFF",
                  textOnColor: "#0A0A0A",
                  image: jugendMillingenLogoAsset,
                },
                {
                  name: "Jugend JSG Emmerich",
                  short: "JSG Emmerich",
                  url: "https://www.instagram.com/jsg_emmerich_jugend/",
                  color: "#FFFFFF",
                  textOnColor: "#0A0A0A",
                  image: jsgEmmerichLogoAsset,
                },
                {
                  name: "Jugend VfB Rheingold",
                  short: "Jugend VfB Rheingold",
                  url: "https://www.vfb-rheingold-07.com/",
                  color: "#FFFFFF",
                  textOnColor: "#0A0A0A",
                  image: vfbRheingoldLogoAsset,
                },
              ].map((v) => {
                const isLink = v.url && v.url !== "#";
                const Tag: any = isLink ? "a" : "div";
                const linkProps = isLink
                  ? { href: v.url, target: "_blank", rel: "noopener noreferrer" }
                  : {};
                return (
                  <Tag
                    key={v.name}
                    {...linkProps}
                    className={`group rounded-2xl bg-white border border-black/10 overflow-hidden flex flex-col transition-all duration-300 ${
                      isLink
                        ? "hover:-translate-y-1 hover:border-[#4CAF50] hover:shadow-[0_8px_24px_-12px_rgba(76,175,80,0.4)] cursor-pointer"
                        : "opacity-90"
                    }`}
                  >
                    <div
                      className="aspect-square flex items-center justify-center overflow-hidden"
                      style={{ backgroundColor: v.color }}
                    >
                      {v.image ? (
                        <img
                          src={v.image}
                          alt={`Logo ${v.name}`}
                          loading="lazy"
                          className="w-full h-full object-contain p-4"
                        />
                      ) : (
                        <span
                          className="font-bold uppercase leading-none"
                          style={{
                            color: v.textOnColor,
                            fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
                            letterSpacing: "-1px",
                          }}
                        >
                          {v.short}
                        </span>
                      )}
                    </div>
                    <div className="p-4 flex items-start justify-between gap-3">
                      <span
                        className="font-bold uppercase text-[#0A0A0A] text-[13px] md:text-[14px] leading-[1.2]"
                        style={{ letterSpacing: "-0.2px" }}
                      >
                        {v.name}
                      </span>
                      {isLink && (
                        <i className="ri-arrow-right-up-line text-[#4CAF50] text-lg flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      )}
                    </div>
                  </Tag>
                );
              })}
            </div>

          </div>
        </section>

        {/* 6. CTA */}
        <section className="relative py-36 md:py-40 px-6 md:px-10 lg:px-14 overflow-hidden">
          <img
            src={ctaLernenBgAsset}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#4CAF50]/80 mix-blend-multiply" />
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative max-w-5xl mx-auto text-center">
            <h2
              data-split-reveal
              className="font-bold uppercase text-white leading-[0.95] break-words"
              style={{
                fontSize: "clamp(1.875rem, 8vw, 5.25rem)",
                letterSpacing: "clamp(-0.6px, -0.18vw, -2.4px)",
              }}
            >
              Lernen Sie uns kennen
            </h2>
            <p className="mt-6 text-white/90 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Kostenlose Erstberatung — unverbindlich und ehrlich.
            </p>
            <a
              href="/kontakt"
              className="btn-slide btn-fill-white inline-flex items-center mt-10 px-8 py-4 rounded-full bg-[#0A0A0A] text-white text-sm font-medium"
            >
              Erstberatung anfragen
            </a>
          </div>
        </section>

        {/* 7. CROSS-LINK */}
        <section className="bg-[#F4F4F4] py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden bg-white aspect-[4/3] md:order-1 order-2">
              <img
                src="https://www.zweering-elektrotechnik.de/wp-content/uploads/2025/11/IMG_0099.webp"
                alt="Zweering Elektrotechnik — Leistungen aus einer Hand in Emmerich am Rhein"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="md:order-2 order-1">
              <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                Das könnte Sie auch interessieren
              </p>
            <h2
              data-split-reveal
              className="font-bold uppercase text-[#0A0A0A] leading-[0.95] max-w-3xl break-words"
              style={{
                fontSize: "clamp(1.5rem, 6vw, 3.5rem)",
                letterSpacing: "clamp(-0.5px, -0.15vw, -2px)",
              }}
            >
                Unsere Leistungen
              </h2>
              <p className="mt-6 text-foreground-600 text-base md:text-lg leading-relaxed">
                Von Photovoltaik bis Smart Home — sehen Sie, womit wir Ihr Zuhause
                ausstatten.
              </p>
              <a
                href="/photovoltaik"
                className="inline-flex items-center mt-8 px-6 py-3.5 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#4CAF50] transition-colors"
              >
                Zu den Leistungen →
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
